import makeRequest from '#shared/util/makeRequest.js';
import checkTypes from '#shared/util/checkTypes.js';
import env from '#shared/config/env.js';
import envLocal from '#shared/config/env/local.js';

/**
 * @class Cart
 */
export class Cart {

	/**
	 * Construct the class
	 * @param {object} provider Checkout options
	 * @param {string} provider.apiEndpoint Checkout cart API endpoint
	 * @param {object} session Checkout session object
	 * @param {string} token Checkout session token
	 * @param {string} requestHash Checkout request hash
	 * @public
	 * @memberof Cart
	 */
	constructor(provider = {}, session = {}, token = '', requestHash = '') {
		checkTypes('Cart.constructor', {
			provider: [provider, 'object', true],
			'provider.apiEndpoint': [provider.apiEndpoint, 'string'],
			session: [session, 'object', true],
			token: [token, 'string']
		});
		this.provider = Object.assign({}, provider);
		this.session = Object.assign({}, session);
		this.token = token;
		this.requestHash = requestHash;
	}

	/**
	 * Make API request
	 * @param {string} path Request path
	 * @param {object} [options] Request options
	 * @param {string} [options.method] Request method
	 * @param {string} [options.hostname] Request hostname
	 * @param {object} [options.form] Send application/x-www-form-urlencoded
	 * @param {object} [options.json] Send application/json
	 * @param {object} [options.query] Object of query parameters
	 * @returns {Promise<object>}
	 * @private
	 * @memberof Cart
	 */
	async _request(path = '', options = {}) {
		checkTypes('Cart._request', {
			path: [path, 'string'],
			options: [options, 'object']
		});
		const fetchOptions = Object.assign({}, options);
		fetchOptions.method = fetchOptions.method || 'GET';
		fetchOptions.hostname = fetchOptions.hostname || this.provider.apiEndpoint;
		fetchOptions.credentials = fetchOptions.credentials || 'same-origin';
		// Configure required headers
		fetchOptions.headers = fetchOptions.headers || {};
		// Configure custom request hash header
		if (this.requestHash) {
			fetchOptions.headers['x-checkout-origin'] = this.requestHash;
		}
		// Configure authorization header with checkout token
		if (this.token) {
			fetchOptions.headers.authorization = `Bearer ${this.token}`;
		}
		// Make the actual request and return response body
		const response = await makeRequest(`${fetchOptions.hostname}${path}`, fetchOptions, this.session);
		return response.body;
	}

	/**
	 * Open a fresh cart
	 * @param {object} cartData Cart data (products, quantitites, etc.)
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	openCart(cartData) {
		return this._request('/cart', {
			method: 'POST',
			json: cartData
		});
	}

	/**
	 * Get cart data
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	getCart() {
		return this._request('/cart');
	}

	/**
	 * Apply billing address to the cart
	 * @param {object} [billing]
	 * @param {string} [billing.firstName]
	 * @param {string} [billing.lastName]
	 * @param {string} [billing.email]
	 * @param {string} [billing.city]
	 * @param {string} [billing.country]
	 * @param {string} [billing.countryName]
	 * @param {string} [billing.state]
	 * @param {string} [billing.zip]
	 * @param {string} [billing.companyName]
	 * @param {string} [billing.line1]
	 * @param {string} [billing.line2]
	 * @param {string} [billing.phoneNumber]
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	applyBilling(billing = null) {
		return this._request('/cart/billing', {
			method: 'POST',
			json: billing
		});
	}

	/**
	 * Remove billing address from the cart
	 * @note Used for PayPal where we remove any billing from the cart first and then apply billing returned from PayPal
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	removeBilling() {
		return this._request('/cart/billing', {
			method: 'DELETE'
		});
	}

	/**
	 * Apply company details to the cart
	 * @param {object} [company]
	 * @param {string} [company.companyName]
	 * @param {string} [company.taxId]
	 * @param {string} [company.line1]
	 * @param {string} [company.line2]
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	applyCompany(company = null) {
		return this._request('/cart/company', {
			method: 'POST',
			json: company
		});
	}

	/**
	 * Apply form details to the cart
	 * @param {object} [form]
	 * @param {number} [form.installments]
	 * @param {object} [form.nationalId]
	 * @param {string} [form.nationalId.type]
	 * @param {object} [form.nationalId.value]
	 * @param {string} [form.nationalId.value.formatted]
	 * @param {string} [form.nationalId.value.unformatted]
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	applyForm(form = null) {
		return this._request('/cart/form', {
			method: 'POST',
			json: form
		});
	}

	/**
	 * Apply payment source to the cart
	 * @param {object} paymentSource
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	applyPayment(paymentSource) {
		return this._request('/cart/payment', {
			method: 'POST',
			json: paymentSource
		});
	}

	/**
	 * Submit the order
	 * @param {object} submitOrderPayload
	 * @param {object} submitOrderPayload.browserInformation
	 * @returns {Promise} Cart data
	 * @public
	 * @memberof Cart
	 */
	submitOrder(submitOrderPayload = {}) {
		return this._request('/cart/submit', {
			method: 'POST',
			json: submitOrderPayload
		});
	}

	/**
	 * Add product to cart
	 * @param {object} options
	 * @param {string} options.product Product ID
	 * @param {number} options.quantity New product quantity
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	addProduct({ product, quantity } = {}) {
		return this._request(`/cart/product/${product}/${quantity}`, {
			method: 'POST'
		});
	}

	/**
	 * Update quantity of item in cart
	 * @param {object} options
	 * @param {string} options.product Product ID
	 * @param {number} options.quantity New product quantity
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	updateProductQuantity({ product, quantity } = {}) {
		return this._request(`/cart/product/${product}/quantity/${quantity}`, {
			method: 'PUT'
		});
	}

	/**
	 * Replace product in cart, eg. variation change
	 * @param {object} options
	 * @param {string} options.oldProduct ID of product to be removed
	 * @param {string} options.newProduct ID of product to be added
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	replaceProduct({ oldProduct, newProduct } = {}) {
		return this._request(`/cart/product/${oldProduct}/variant/${newProduct}`, {
			method: 'PUT'
		});
	}

	/**
	 * Remove product from cart
	 * @param {object} options
	 * @param {string} options.product Product ID
	 * @returns {Promise}
	 * @public
	 * @memberof Cart
	 */
	removeProduct({ product } = {}) {
		return this._request(`/cart/product/${product}`, {
			method: 'DELETE'
		});
	}

}

/**
 * Cart model factory
 * @param {object} state
 * @returns {Cart}
 */
export const cartFactory = (state) => {
	// Define provider options
	const providerConfig = Object.assign({}, env.providers.checkout.variables);
	// (localhost only) Force checkout model to be localhost no matter the env settings
	if (env.isLocalhost) {
		Object.assign(providerConfig, envLocal.providers.checkout.variables);
	}
	// Return new model instance
	return new Cart(providerConfig, state.app?.session || {}, state.app?.token || '', state.app?.requestHash || '-');
};
